import React, { useState } from 'react';
import { useFilters } from '../../contexts/FiltersContext';

const Section: React.FC<{ title: string; icon?: string; children: React.ReactNode }> = ({ title, icon, children }) => (
  <div className="mb-4 last:mb-0">
    <div className="flex items-center gap-2 mb-2">
      {icon && <span className="text-lg">{icon}</span>}
      <h3 className="text-xs font-semibold text-slate-800 uppercase tracking-wider">{title}</h3>
    </div>
    <div className="bg-white rounded-lg p-3 shadow-sm border border-slate-100">
      {children}
    </div>
  </div>
);

const TopFilterBar: React.FC = () => {
  const { filters, updateFilter, clearFilters } = useFilters();

  // Local state for inputs - only committed on blur/Enter
  const [localPriceMin, setLocalPriceMin] = useState('');
  const [localPriceMax, setLocalPriceMax] = useState('');
  const [localSqftMin, setLocalSqftMin] = useState('');
  const [localSqftMax, setLocalSqftMax] = useState('');
  const [localCashFlowMin, setLocalCashFlowMin] = useState('');
  const [localCapRateMin, setLocalCapRateMin] = useState('');
  const [localCocReturnMin, setLocalCocReturnMin] = useState('');
  const [localRoiMin, setLocalRoiMin] = useState('');

  const hasActiveFilters = Object.values(filters).some(v => 
    Array.isArray(v) ? v.length > 0 : v !== undefined && v !== ''
  );

  const commitNumber = (key: string, value: string) => {
    const trimmed = value.trim();
    updateFilter(key as any, trimmed ? Number(trimmed) : undefined);
  };

  const currentStatuses = filters.status || ['for_sale', 'ready_to_build'];
  const currentTypes = filters.property_type || [];

  const toggleStatus = (value: string) => {
    const next = currentStatuses.includes(value)
      ? currentStatuses.filter(s => s !== value)
      : [...currentStatuses, value];
    if (next.length > 0) updateFilter('status', next);
  };

  const toggleType = (value: string) => {
    const next = currentTypes.includes(value)
      ? currentTypes.filter(t => t !== value)
      : [...currentTypes, value];
    updateFilter('property_type', next.length > 0 ? next : undefined);
  };

  const inputCls = 'w-full px-4 py-2.5 text-sm bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:bg-white outline-none transition-all duration-200 placeholder:text-slate-400';
  const selectCls = 'w-full px-4 py-2.5 text-sm bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:bg-white outline-none transition-all duration-200 cursor-pointer';

  return (
    <div className="w-80 bg-gradient-to-b from-slate-50 to-white border-r border-slate-200 overflow-y-auto shadow-sm">
      <div className="p-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-xl font-bold text-slate-900 tracking-tight">Filters</h2>
            {hasActiveFilters && (
              <button
                onClick={() => {
                  clearFilters();
                  setLocalPriceMin('');
                  setLocalPriceMax('');
                  setLocalSqftMin('');
                  setLocalSqftMax('');
                  setLocalCashFlowMin('');
                }}
                className="px-3 py-1.5 text-xs font-semibold text-white bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 rounded-full shadow-sm transition-all duration-200 hover:shadow-md"
              >
                Clear All
              </button>
            )}
          </div>
          <p className="text-xs text-slate-500">Refine your property search</p>
        </div>
        
        {/* Price Range */}
        <Section title="Price Range" icon="💰">
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs">$</span>
              <input
                type="number"
                value={localPriceMin}
                onChange={(e) => setLocalPriceMin(e.target.value)}
                onBlur={() => commitNumber('price_min', localPriceMin)}
                onKeyDown={(e) => e.key === 'Enter' && commitNumber('price_min', localPriceMin)}
                placeholder="Min"
                className={`${inputCls} pl-6`}
              />
            </div>
            <span className="text-slate-400 text-sm">—</span>
            <div className="relative flex-1">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs">$</span>
              <input
                type="number"
                value={localPriceMax}
                onChange={(e) => setLocalPriceMax(e.target.value)}
                onBlur={() => commitNumber('price_max', localPriceMax)}
                onKeyDown={(e) => e.key === 'Enter' && commitNumber('price_max', localPriceMax)}
                placeholder="Max"
                className={`${inputCls} pl-6`}
              />
            </div>
          </div>
        </Section>

        {/* Investment Minimums */}
        <Section title="Investment Minimums" icon="💵">
          <div className="space-y-2">
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs">$</span>
              <input
                type="number"
                value={localCashFlowMin}
                onChange={(e) => setLocalCashFlowMin(e.target.value)}
                onBlur={() => commitNumber('cash_flow_min', localCashFlowMin)}
                onKeyDown={(e) => e.key === 'Enter' && commitNumber('cash_flow_min', localCashFlowMin)}
                placeholder="Min cash flow/mo"
                className={`${inputCls} pl-6 pr-10`}
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs">/mo</span>
            </div>
            <div className="relative">
              <input
                type="number"
                value={localCapRateMin}
                onChange={(e) => setLocalCapRateMin(e.target.value)}
                onBlur={() => commitNumber('cap_rate_min', localCapRateMin)}
                onKeyDown={(e) => e.key === 'Enter' && commitNumber('cap_rate_min', localCapRateMin)}
                placeholder="Min cap rate %"
                className={`${inputCls} pr-8`}
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs">%</span>
            </div>
            <div className="relative">
              <input
                type="number"
                value={localCocReturnMin}
                onChange={(e) => setLocalCocReturnMin(e.target.value)}
                onBlur={() => commitNumber('coc_return_min', localCocReturnMin)}
                onKeyDown={(e) => e.key === 'Enter' && commitNumber('coc_return_min', localCocReturnMin)}
                placeholder="Min CoC return %"
                className={`${inputCls} pr-8`}
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs">%</span>
            </div>
            <div className="relative">
              <input
                type="number"
                value={localRoiMin}
                onChange={(e) => setLocalRoiMin(e.target.value)}
                onBlur={() => commitNumber('roi_min', localRoiMin)}
                onKeyDown={(e) => e.key === 'Enter' && commitNumber('roi_min', localRoiMin)}
                placeholder="Min ROI %"
                className={`${inputCls} pr-8`}
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs">%</span>
            </div>
          </div>
        </Section>

        {/* Beds & Baths */}
        <Section title="Beds & Baths" icon="🛏️">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1.5">Beds</label>
              <select
                value={filters.bedrooms_min || ''}
                onChange={(e) => updateFilter('bedrooms_min', e.target.value ? Number(e.target.value) : undefined)}
                className={selectCls}
              >
                <option value="">Any</option>
                {[1,2,3,4,5].map(n => <option key={n} value={n}>{n}+</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1.5">Baths</label>
              <select
                value={filters.baths_min || ''}
                onChange={(e) => updateFilter('baths_min', e.target.value ? Number(e.target.value) : undefined)}
                className={selectCls}
              >
                <option value="">Any</option>
                {[1,1.5,2,2.5,3,3.5,4].map(n => <option key={n} value={n}>{n}+</option>)}
              </select>
            </div>
          </div>
        </Section>

        {/* Square Footage */}
        <Section title="Square Footage" icon="📐">
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <input
                type="number"
                value={localSqftMin}
                onChange={(e) => setLocalSqftMin(e.target.value)}
                onBlur={() => commitNumber('sqft_min', localSqftMin)}
                onKeyDown={(e) => e.key === 'Enter' && commitNumber('sqft_min', localSqftMin)}
                placeholder="Min"
                className={inputCls}
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs">ft²</span>
            </div>
            <span className="text-slate-400 text-sm">—</span>
            <div className="relative flex-1">
              <input
                type="number"
                value={localSqftMax}
                onChange={(e) => setLocalSqftMax(e.target.value)}
                onBlur={() => commitNumber('sqft_max', localSqftMax)}
                onKeyDown={(e) => e.key === 'Enter' && commitNumber('sqft_max', localSqftMax)}
                placeholder="Max"
                className={inputCls}
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs">ft²</span>
            </div>
          </div>
        </Section>

        {/* Property Type */}
        <Section title="Property Type" icon="🏡">
          <div className="flex flex-wrap gap-2">
            {[
              { value: 'single_family', label: 'Single Family' },
              { value: 'multi_family', label: 'Multi-Family' },
              { value: 'condos', label: 'Condo' },
              { value: 'townhomes', label: 'Townhouse' },
              { value: 'mobile', label: 'Mobile' },
              { value: 'land', label: 'Land' },
            ].map(({ value, label }) => {
              const isSelected = currentTypes.includes(value);
              return (
                <button
                  key={value}
                  onClick={() => toggleType(value)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    isSelected
                      ? 'bg-blue-600 text-white shadow-md hover:bg-blue-700'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200 border border-slate-200'
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </Section>

        {/* Status */}
        <Section title="Listing Status" icon="📊">
          <div className="flex flex-wrap gap-2">
            {[
              { value: 'for_sale', label: 'For Sale', colorBg: 'bg-green-600', colorHover: 'hover:bg-green-700', colorInactive: 'bg-green-50 text-green-700 border-green-200' },
              { value: 'pending', label: 'Pending', colorBg: 'bg-yellow-600', colorHover: 'hover:bg-yellow-700', colorInactive: 'bg-yellow-50 text-yellow-700 border-yellow-200' },
              { value: 'contingent', label: 'Contingent', colorBg: 'bg-orange-600', colorHover: 'hover:bg-orange-700', colorInactive: 'bg-orange-50 text-orange-700 border-orange-200' },
              { value: 'sold', label: 'Sold', colorBg: 'bg-red-600', colorHover: 'hover:bg-red-700', colorInactive: 'bg-red-50 text-red-700 border-red-200' },
              { value: 'ready_to_build', label: 'Ready to Build', colorBg: 'bg-blue-600', colorHover: 'hover:bg-blue-700', colorInactive: 'bg-blue-50 text-blue-700 border-blue-200' },
            ].map(({ value, label, colorBg, colorHover, colorInactive }) => {
              const isSelected = currentStatuses.includes(value);
              return (
                <button
                  key={value}
                  onClick={() => toggleStatus(value)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    isSelected
                      ? `${colorBg} ${colorHover} text-white shadow-md`
                      : `${colorInactive} hover:opacity-80 border`
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </Section>
      </div>
    </div>
  );
};

export default TopFilterBar;
